import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Info, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Header } from './Header';


const SavedAnalysisDashboard = () => {
    const { id } = useParams();
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                const response = await get({
                    apiName: 'dcf',
                    path: `/analysis/${id}`
                }).response;

                const reader = response.body.getReader();
                const { value } = await reader.read();
                const text = new TextDecoder().decode(value);
                const data = JSON.parse(text);

                setAnalysis(data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching analysis:', err);
                setError('Failed to fetch analysis. Please try again.');
                setLoading(false);
            }
        };

        fetchAnalysis();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;
    if (!analysis) return <div>No analysis found</div>;

    const formatCurrency = (value) => {
        if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        return `$${value.toFixed(2)}`;
    };

    const MetricCard = ({ label, value, prefix = '', suffix = '', trend = null, description = '' }) => (
        <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{label}</span>
                        {description && (
                            <div className="relative group">
                                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                <div className="hidden group-hover:block absolute z-50 w-48 p-2 bg-white border rounded shadow-lg text-xs">
                                    {description}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-2xl font-bold">
                    {prefix}{typeof value === 'number' ? value.toFixed(2) : value}{suffix}
                </div>
            </CardContent>
        </Card>
    );

    const { company_info, fcf_analysis, valuation, createdBy } = analysis.analysis;
    const waccValues = [valuation.wacc - 2, valuation.wacc, valuation.wacc + 2];
    const growthValues = [valuation.growthRate - 2, valuation.growthRate, valuation.growthRate + 2];

    const calculateValuation = (waccRate, growthRate) => {
        const baseFcf = fcf_analysis[0].fcf;
        const wacc = waccRate / 100;
        const growth = growthRate / 100;
        const terminalGrowth = valuation.terminalGrowth / 100;

        let totalPv = 0;
        let projectedFcf = baseFcf;

        for (let year = 1; year <= 4; year++) {
            projectedFcf *= (1 + growth);
            totalPv += projectedFcf / Math.pow(1 + wacc, year);
        }

        const terminalFcf = projectedFcf * (1 + terminalGrowth);
        const terminalValue = terminalFcf / (wacc - terminalGrowth);
        const discountedTerminalValue = terminalValue / Math.pow(1 + wacc, 4);

        const enterpriseValue = totalPv + discountedTerminalValue;
        const fairValue = enterpriseValue / valuation.sharesOutstanding;

        return { enterpriseValue, fairValue };
    };

    return (<>
        <Header
            companyName={company_info.name}
            ticker={company_info.ticker}
            isAnalyzing={false}
            isAuthenticated={false} // Set this based on your auth state
        />
        <div className="p-6 max-w-7xl mx-auto">
            {/* Overview Section */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <div className="flex justify-between items-start">
                            <div>
                                <CardTitle>{company_info.name} ({company_info.ticker})</CardTitle>
                                <CardDescription>{company_info.sector} | {company_info.industry}</CardDescription>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                                <div>Created by : {createdBy}</div>
                                <div>Created: {new Date(analysis.created_at).toLocaleDateString()}</div>
                            </div>
                        </div>
                        <div className="border-t pt-4">
                            <p className="text-sm text-gray-600 leading-relaxed">{company_info.description}</p>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                            <MetricCard label="Fair Value" value={valuation.fairValue} prefix="$"
                                description="Estimated intrinsic value based on DCF analysis" />
                            <MetricCard label="Current Price" value={valuation.currentPrice} prefix="$" />
                            <MetricCard label="Margin of Safety" value={valuation.marginOfSafety} suffix="%"
                                trend={valuation.marginOfSafety} />
                        </div>
                        {/* Investment Thesis */}
                        <div className="mt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Investment Thesis</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="font-mono text-sm whitespace-pre-line">
                                        {analysis.analysis.analyst_thesis || 'No investment thesis provided.'}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </CardContent>
                </Card>
            </section>

            {/* Historical FCF Section */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Historical Cash Flow Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={fcf_analysis}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" />
                                    <YAxis tickFormatter={formatCurrency} />
                                    <Tooltip formatter={formatCurrency} />
                                    <Legend />
                                    <Bar dataKey="fcf" name="Free Cash Flow" fill="#2563eb" />
                                    <Bar dataKey="ocf" name="Operating Cash Flow" fill="#4ade80" />
                                    <Bar dataKey="capex" name="Capital Expenditure" fill="#f87171" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </section>
            {/* Valuation Parameters */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Valuation Parameters</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-4">
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span>WACC</span>
                                    <span>{valuation.wacc.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span>Terminal Growth Rate</span>
                                    <span>{valuation.terminalGrowth.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span>Growth Rate</span>
                                    <span>{valuation.growthRate.toFixed(1)}%</span>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span>Enterprise Value</span>
                                    <span>{formatCurrency(valuation.enterpriseValue)}</span>
                                </div>
                                <div className="flex justify-between p-2 bg-gray-50 rounded">
                                    <span>Shares Outstanding</span>
                                    <span>{formatCurrency(valuation.sharesOutstanding)}</span>
                                </div>
                                <div className="flex justify-between p-2 bg-blue-50 rounded font-bold">
                                    <span>Fair Value per Share</span>
                                    <span>${valuation.fairValue.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </section>
            {/* DCF Table */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Discounted Cash Flow Analysis</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr>
                                        <th className="border p-2 bg-gray-50 text-left">Year</th>
                                        <th className="border p-2 bg-gray-50 text-right">Growth Rate</th>
                                        <th className="border p-2 bg-gray-50 text-right">Projected FCF</th>
                                        <th className="border p-2 bg-gray-50 text-right">Discount Factor</th>
                                        <th className="border p-2 bg-gray-50 text-right">Present Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4].map(year => {
                                        const baseFcf = fcf_analysis[0].fcf;
                                        let projectedFcf = baseFcf * Math.pow(1 + valuation.growthRate / 100, year);
                                        const discountFactor = Math.pow(1 + valuation.wacc / 100, year);
                                        const pv = projectedFcf / discountFactor;

                                        return (
                                            <tr key={year} className="hover:bg-gray-50">
                                                <td className="border p-2">{year}</td>
                                                <td className="border p-2 text-right">{valuation.growthRate.toFixed(1)}%</td>
                                                <td className="border p-2 text-right">{formatCurrency(projectedFcf)}</td>
                                                <td className="border p-2 text-right">{discountFactor.toFixed(3)}</td>
                                                <td className="border p-2 text-right">{formatCurrency(pv)}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr className="bg-gray-50 font-medium">
                                        <td className="border p-2">Terminal Value</td>
                                        <td className="border p-2 text-right">{valuation.terminalGrowth}% perpetual growth</td>
                                        <td className="border p-2 text-right" colSpan={2}>
                                            {Math.pow(1 + valuation.wacc / 100, 5).toFixed(3)}
                                        </td>
                                        <td className="border p-2 text-right">
                                            {formatCurrency(valuation.enterpriseValue * 0.7)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </section>

            {/* Sensitivity Analysis */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Sensitivity Analysis</CardTitle>
                        <CardDescription>Impact of WACC and Growth Rate changes on valuation</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {/* Enterprise Value Matrix */}
                        <div className="mt-6">
                            <h3 className="text-lg font-medium mb-4">Enterprise Value Sensitivity Matrix</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                    <thead>
                                        <tr>
                                            <th className="border p-2 bg-gray-50">WACC ↓ Growth →</th>
                                            {growthValues.map((growth) => (
                                                <th key={growth} className="border p-2 bg-gray-50 text-right">
                                                    {growth.toFixed(1)}%
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {waccValues.map((wacc) => (
                                            <tr key={wacc}>
                                                <td className="border p-2 bg-gray-50">{wacc.toFixed(1)}%</td>
                                                {growthValues.map((growth) => {
                                                    const { enterpriseValue } = calculateValuation(wacc, growth);
                                                    const variance = ((enterpriseValue - valuation.enterpriseValue) / valuation.enterpriseValue) * 100;
                                                    return (
                                                        <td key={`${wacc}-${growth}`}
                                                            className={`border p-2 text-right ${variance > 10 ? 'bg-green-50' : variance < -10 ? 'bg-red-50' : ''}`}>
                                                            {formatCurrency(enterpriseValue)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Share Price Matrix */}
                        <div className="mt-6">
                            <h3 className="text-lg font-medium mb-4">Share Price Sensitivity Matrix</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                    <thead>
                                        <tr>
                                            <th className="border p-2 bg-gray-50">WACC ↓ Growth →</th>
                                            {growthValues.map((growth) => (
                                                <th key={growth} className="border p-2 bg-gray-50 text-right">
                                                    {growth.toFixed(1)}%
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {waccValues.map((wacc) => (
                                            <tr key={wacc}>
                                                <td className="border p-2 bg-gray-50">{wacc.toFixed(1)}%</td>
                                                {growthValues.map((growth) => {
                                                    const { fairValue } = calculateValuation(wacc, growth);
                                                    const variance = ((fairValue - valuation.fairValue) / valuation.fairValue) * 100;
                                                    return (
                                                        <td key={`${wacc}-${growth}`}
                                                            className={`border p-2 text-right ${variance > 10 ? 'bg-green-50' : variance < -10 ? 'bg-red-50' : ''}`}>
                                                            ${fairValue.toFixed(2)}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </section>



            {/* Historical Growth Trends */}
            <section className="pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Historical Growth Trends</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={fcf_analysis}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="year" />
                                    <YAxis yAxisId="left" tickFormatter={(value) => `${value}%`} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={formatCurrency} />
                                    <Tooltip />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="fcfMargin" name="FCF Margin" stroke="#82ca9d" />
                                    <Line yAxisId="left" type="monotone" dataKey="growth_rate" name="Growth Rate" stroke="#8884d8" />
                                    <Line yAxisId="right" type="monotone" dataKey="fcf" name="Free Cash Flow" stroke="#ff7300" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </section>
        </div>        </>
    );
};

export default SavedAnalysisDashboard;